// src/pages/vendor/Pickups/index.tsx

import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Calendar as CalendarIcon, X, Package } from 'lucide-react';
import PickupCard from '../../../components/vendor/PickupCard';
// import PickupStatusBadge from '../../../components/vendor/PickupStatusBadge';
import type { Pickup } from '../../../types';
import PickupForm from '../../../components/vendor/pickup/PickupForm';
import { vendorAPI } from '../../../api/vendor';
import pickupsAPI from '../../../api/pickup';
import { useVendor } from '../../../hooks/useVendor';
import { useNavigate } from 'react-router-dom';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import toast from '../../../utils/toast';

const Pickups: React.FC = () => {
    const navigate = useNavigate();
    const [pickups, setPickups] = useState<Pickup[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'all' | 'scheduled' | 'active' | 'completed' | 'cancelled'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showNewPickupModal, setShowNewPickupModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const { hasMultipleStores } = useVendor();
    const [resumePickup, setResumePickup] = useState<Pickup | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrev, setHasPrev] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [cancelTargetId, setCancelTargetId] = useState<number | null>(null);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

    const confirmCancel = async () => {
        if (!cancelTargetId) return;
        setIsCancelling(true);
        try {
            await pickupsAPI.cancelPickup(cancelTargetId);
            toast.success('Pickup request cancelled');
            setPickups(prev => prev.map(p =>
                p.id === cancelTargetId ? { ...p, status: 'cancelled' } : p
            ));
            setShowCancelModal(false);
            setCancelTargetId(null);
        } catch (err) {
            console.error('Cancel failed:', err);
        } finally {
            setIsCancelling(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTargetId) return;
        setIsDeleting(true);
        try {
            await pickupsAPI.deletePickup(deleteTargetId);
            toast.success('Pickup request deleted');
            setPickups(prev => prev.filter(p => p.id !== deleteTargetId));
            setShowDeleteModal(false);
            setDeleteTargetId(null);
        } catch (err) {
            console.error('Delete failed:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        fetchPickups();
    }, [activeTab]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(searchQuery), 300);
        return () => clearTimeout(t);
    }, [searchQuery]);

    useEffect(() => {
        setPage(1);
        fetchPickups(1, debouncedQuery);
    }, [debouncedQuery]);

    const handleCreatePickup = async (data: any) => {
        setIsProcessing(true);
        try {
            // TODO: API call to create pickup
            await vendorAPI.createPickup(data);
            setShowNewPickupModal(false);
            await fetchPickups();
            // Toast success
        } catch (error) {
            console.error('Failed to create pickup:', error);
            // Toast error
        } finally {
            setIsProcessing(false);
        }
    };

    const fetchPickups = async (pageNum = 1, query = '') => {
        setLoading(true);
        try {
            const data = await pickupsAPI.getPickupRequests({ page: pageNum, search: query, status: activeTab });
            setPickups(data.results);
            setTotalPages(Math.max(1, Math.ceil(data.count / (data.page_size || 12))));
            setHasNext(!!data.next);
            setHasPrev(!!data.previous);
        } catch (err) {
            console.error('Failed to fetch pickups:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPickups(page, searchQuery);
    }, [page]);

    const getStatusCounts = () => {
        const counts = {
            all: pickups.length,
            draft: 0,
            pending: 0,
            in_transit: 0,
            received: 0,
            cancelled: 0,
            rejected: 0,
        };
        pickups.forEach(p => {
            if (p.status in counts) {
                counts[p.status as keyof typeof counts] += 1;
            }
        });
        return counts;
    };

    const statusCounts = getStatusCounts();

    const tabs = [
        { id: 'all', label: 'All', count: statusCounts.all },
        { id: 'draft', label: 'Drafts', count: statusCounts.draft },
        { id: 'pending', label: 'Pending', count: statusCounts.pending },
        { id: 'in_transit', label: 'In Transit', count: statusCounts.in_transit },
        { id: 'received', label: 'Received', count: statusCounts.received },
        { id: 'cancelled', label: 'Cancelled', count: statusCounts.cancelled },
        { id: 'rejected', label: 'Rejected', count: statusCounts.rejected },
    ];
    const handleViewDetails = (id: number) => navigate(`/vendor/pickups/${id}`)

    const handleContinueDraft = async (id: number) => {
        try {
            const draft = await pickupsAPI.getPickupRequest(id);
            setResumePickup(draft);
            setShowNewPickupModal(true);
        } catch (err) {
            console.error('Failed to load draft:', err);
        }
    };

    const handleClose = () => {
        setShowNewPickupModal(false);
        setResumePickup(null);
        // fetchPickups();  // refresh list
    };

    return (
    <div className="space-y-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Pickups</h2>
                <p className="text-sm text-gray-500">Manage your pickup requests</p>
            </div>
            <button
                onClick={() => setShowNewPickupModal(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
                <Plus size={18} className="mr-1.5" />
                New Pickup
            </button>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                    type="text"
                    placeholder="Search by request number, store, or address..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {loading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent" />
                    </div>
                )}
            </div>
            <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
                <Filter className="h-4 w-4" />
                {activeTab !== 'all' && (
                    <span className="w-2 h-2 bg-blue-600 rounded-full" />
                )}
            </button>
        </div>

        {/* Filters */}
        {showFilters && (
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setActiveTab(tab.id as typeof activeTab);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            activeTab === tab.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                    >
                        {tab.label}
                        {/* <span className={`ml-1.5 ${activeTab === tab.id ? 'text-blue-100' : 'text-gray-500'}`}>
                            {tab.count}
                        </span> */}
                    </button>
                ))}
            </div>
        )}

        {/* Pickups List */}
        <div>
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            ) : pickups.length === 0 ? (
                <div className="text-center py-12">
                    <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No pickups found</p>
                    <p className="text-sm text-gray-400 mt-1">
                        {searchQuery ? 'Try adjusting your search or filters' : 'Create your first pickup request'}
                    </p>
                    {!searchQuery && (
                        <button
                            onClick={() => setShowNewPickupModal(true)}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                            + New Pickup
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pickups.map((pickup) => (
                                <PickupCard
                                    key={pickup.id}
                                    pickup={pickup}
                                    onViewDetails={handleViewDetails}
                                    hasMultipleStores={hasMultipleStores}
                                    onContinue={handleContinueDraft}
                                    onCancel={(id) => {
                                        setCancelTargetId(id);
                                        setShowCancelModal(true);
                                    }}
                                    onDelete={(id) => {
                                        setDeleteTargetId(id);
                                        setShowDeleteModal(true);
                                    }}
                                />
                    ))}
                </div>
            )}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={!hasPrev}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={!hasNext}
                            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* New Pickup Modal */}
            {showNewPickupModal && (
                <PickupForm
                    isOpen={showNewPickupModal}
                    onClose={handleClose}
                    onSubmit={handleCreatePickup}
                    isProcessing={isProcessing}
                    resumePickup={resumePickup}
                />
            )}

            <ConfirmationModal
                isOpen={showCancelModal}
                onClose={() => { setShowCancelModal(false); setCancelTargetId(null); }}
                onConfirm={confirmCancel}
                title="Cancel Pickup Request?"
                message="Are you sure you want to cancel this pickup request? This cannot be undone."
                confirmText="Yes, Cancel Pickup"
                cancelText="Keep"
                type="warning"
                isProcessing={isCancelling}
            />

            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => { setShowDeleteModal(false); setDeleteTargetId(null); }}
                onConfirm={confirmDelete}
                title="Delete Draft?"
                message="This will permanently delete this pickup request and its labels. This cannot be undone."
                confirmText="Delete"
                cancelText="Keep"
                type="danger"
                isProcessing={isDeleting}
            />
        </div>
    );
};

export default Pickups;